import {
  ArchiveIcon,
  ClipboardIcon,
  FilePlusIcon,
  PencilIcon,
  PlusIcon,
  SpadeIcon,
} from "lucide-react";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useNavigate } from "react-router";
import type { RetroResponse } from "shared/model/retro/retro.response";
import { UserInTeamResponse } from "shared/model/user/user.response";
import { RetroService } from "@/api/Retro.service";
import { UserService } from "@/api/User.service";
import SlotMachineIcon from "@/assets/icons/slot-machine-icon.svg";
import { UserAvatar } from "@/components/molecules/user_avatar/UserAvatar";
import {
  SLOT_MACHINE_ANIMATION_DURATION,
  SlotMachine,
  SlotMachineRef,
} from "@/components/organisms/slot_machine/SlotMachine";
import { AvatarGroup, AvatarStatus } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDebounce } from "@/hooks/useDebounce";
import { useTeamRole } from "@/hooks/useTeamRole";

interface TeamRetroListProps {
  teamName: string;
  teamId: string;
  openReflectionCardsShelfClick: () => void;
}

export const TeamCard: React.FC<TeamRetroListProps> = ({
  teamName,
  teamId,
  openReflectionCardsShelfClick,
}) => {
  const navigate = useNavigate();
  const { role } = useTeamRole(teamId);
  const [retros, setRetros] = useState<RetroResponse[]>([]);
  const [teamUsers, setTeamUsers] = useState<UserInTeamResponse[]>();
  const isAnyRetroRunning = retros.findIndex((a) => a.is_running) !== -1;

  useEffect(() => {
    RetroService.getRetrosByTeamId(teamId)
      .then((retros) => {
        setRetros(retros);
      })
      .catch(console.error);

    UserService.getUsersByTeamId(teamId)
      .then((users) => {
        setTeamUsers(users);
      })
      .catch(console.error);
  }, [teamId]);

  return (
    <div
      data-testid={`team-${teamName}`}
      className={
        "flex flex-col w-full p-4 sm:p-5 gap-4 bg-card rounded-xl border border-border/70 shadow-sm"
      }
    >
      <div
        className={
          "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        }
      >
        <div
          className={
            "flex flex-row flex-wrap gap-2 font-bold text-xl items-center"
          }
        >
          {teamName}

          {teamUsers && (
            <AvatarGroup>
              {teamUsers.map((user) => (
                <Tooltip key={user.id}>
                  <TooltipTrigger
                    render={
                      <UserAvatar
                        avatarUrl={user.avatar_link}
                        name={user.nick}
                      />
                    }
                  />
                  <TooltipContent>{user.nick}</TooltipContent>
                </Tooltip>
              ))}
            </AvatarGroup>
          )}
        </div>

        <div className={"flex flex-wrap justify-end gap-2"}>
          {role !== "USER" && (
            <Button
              data-testid="edit-team"
              onClick={() => navigate(`/team/${teamId}/edit`)}
              size="sm"
            >
              Edytuj
              <PencilIcon className={"size-4"} />
            </Button>
          )}
          <Button
            data-testid="retro-archive"
            onClick={() => navigate(`/team/${teamId}/archive`)}
            size="sm"
          >
            Archiwum
            <ArchiveIcon />
          </Button>
          <Button
            data-testid="open-reflection-cards-shelf"
            onClick={() => openReflectionCardsShelfClick()}
            size="sm"
          >
            Wrzutki
            <FilePlusIcon className={"size-4"} />
          </Button>

          {teamUsers && (
            <Dialog>
              <DialogTrigger
                render={
                  <Button data-testid="open-slot-machine" size="icon">
                    <span className="sr-only">Wylosuj osobę</span>
                    <SlotMachineIcon className={"size-4"} />
                  </Button>
                }
              />
              <SlotMachineDialogContent teamUsers={teamUsers ?? []} />
            </Dialog>
          )}
        </div>
      </div>

      <div className={"grid grid-cols-1 gap-2 sm:grid-cols-3"}>
        <Button
          data-testid="task-list"
          className={"flex-row sm:flex-col min-h-20 sm:min-h-24"}
          onClick={() => navigate(`/team/${teamId}/board`)}
        >
          Lista zadań
          <ClipboardIcon className={"min-size-6 size-6"} />
        </Button>

        <Button
          data-testid="planning-poker"
          className={
            "flex-row sm:flex-col min-h-20 sm:min-h-24 bg-secondary/60 text-secondary-foreground"
          }
          onClick={() => navigate(`/${teamId}/poker`)}
        >
          Planning Poker
          <SpadeIcon />
        </Button>

        {role !== "USER" && !isAnyRetroRunning && (
          <Button
            data-testid="create-retro"
            variant={"destructive"}
            className={"flex-row sm:flex-col min-h-20 sm:min-h-24"}
            onClick={() => navigate(`/retro/create?teamId=${teamId}`)}
          >
            Nowe Retro
            <PlusIcon className={"min-size-6 size-6"} />
          </Button>
        )}

        {retros.map((retro) => {
          if (retro.is_running) {
            return (
              <Button
                data-testid="current-retro"
                key={retro.id}
                className={
                  "flex-row sm:flex-col min-h-20 sm:min-h-24 bg-background text-foreground border-2 border-destructive"
                }
                onClick={() => navigate(`/retro/${retro.id}/reflection`)}
              >
                Retro <br />w trakcie
              </Button>
            );
          } else {
            return null;
          }
        })}
      </div>
    </div>
  );
};

type SlotMachineDialogContentProps = {
  teamUsers: UserInTeamResponse[];
};

const SlotMachineDialogContent: React.FC<SlotMachineDialogContentProps> = ({
  teamUsers,
}) => {
  const [isDrawing, startDrawingTransition] = useTransition();

  const slotMachineRef = useRef<SlotMachineRef>(null);
  const [highlightedUserId, setHighlightedUserId] = useState<string | null>(
    null,
  );
  const highlightedUser = useMemo(
    () => teamUsers.find((u) => u.id === highlightedUserId),
    [highlightedUserId, teamUsers],
  );
  const delayedHighlightedUser = useDebounce(
    highlightedUser,
    SLOT_MACHINE_ANIMATION_DURATION - 200,
  );
  const [teamUsersInPool, setTeamUsersInPool] = useState<string[]>(
    teamUsers.map((u) => u.id),
  );

  const drawMachine = () => {
    const usersInPool = teamUsers.filter((user) =>
      teamUsersInPool.includes(user.id),
    );
    const randomUser =
      usersInPool?.[Math.floor(Math.random() * (usersInPool?.length ?? 1))];
    setHighlightedUserId(randomUser?.id ?? null);

    startDrawingTransition(async () => {
      await slotMachineRef.current?.animateSlotMachine(false);
    });
  };

  return (
    <DialogContent showCloseButton={false} className={"sm:max-w-150"}>
      <DialogHeader>
        <DialogTitle>Losowanie</DialogTitle>
        <DialogDescription>
          Wybierz losowego członka zespołu, wystarczy pociągnąć za dźwignię
        </DialogDescription>
      </DialogHeader>

      <SlotMachine
        className={"mx-auto min-w-100"}
        ref={slotMachineRef}
        onMachineDrawn={() => {
          if (teamUsersInPool.length === 0) return;
          if (isDrawing) return;

          drawMachine();
        }}
        highlightedUserId={highlightedUserId}
        userPool={teamUsers ?? []}
      />

      <div
        className={
          "p-2 bg-secondary/30 rounded h-16 font-semibold flex flex-row gap-2 justify-center items-center"
        }
      >
        {delayedHighlightedUser && (
          <div className={"flex flex-row gap-2 justify-center items-center"}>
            <UserAvatar
              size={"lg"}
              avatarUrl={delayedHighlightedUser.avatar_link}
              name={delayedHighlightedUser.nick}
            />

            <div>{delayedHighlightedUser.nick}</div>
          </div>
        )}
        {!delayedHighlightedUser && <span>. . .</span>}
      </div>

      <div className={"mt-2 gap-2"}>
        <h3 className={"font-semibold"}>Ustawienia losowania</h3>
        <p>wybierz kto jest brany pod uwagę w losowaniu</p>

        <div className={"flex flex-row flex-wrap gap-2 mt-2"}>
          {teamUsers.map((user) => {
            return (
              <div
                key={user.id}
                className={"cursor-pointer"}
                onClick={() => {
                  if (
                    teamUsersInPool.includes(user.id) &&
                    teamUsersInPool.length === 1
                  ) {
                    return;
                  }

                  setTeamUsersInPool((prev) => {
                    if (prev.includes(user.id)) {
                      return prev.filter((id) => id !== user.id);
                    } else {
                      return [...prev, user.id];
                    }
                  });
                }}
              >
                <UserAvatar
                  size={"lg"}
                  avatarUrl={user.avatar_link}
                  name={user.nick}
                >
                  {teamUsersInPool.includes(user.id) && <AvatarStatus />}
                </UserAvatar>
              </div>
            );
          })}
        </div>
      </div>

      <DialogFooter>
        <DialogClose
          render={<Button variant={"destructive"}>Zamknij</Button>}
        />
      </DialogFooter>
    </DialogContent>
  );
};
